import React, { useEffect, useState } from "react";
import { Spinner } from "reactstrap";
import {
  BasicTable,
  Button,
  eventBus,
  Icon,
  textTruncate,
  ToolTips,
} from "@px/px_design_system";
import adunit_nodata from "./icons/adunit_nodata.svg";
import FlatfileAdunits from "./FlatfileAdunits";

export default function AdunitsTable({
  name,
  setValue,
  adunitObjectsMap,
  isAdunitDataRetrieved,
  getValues,
  isEditMode,
}) {
  const [globalFilter, setGlobalFilter] = useState("");
  const [rowSelection, setRowSelection] = useState({});
  const [isTableUpdated, setIsTableUpdated] = useState(false);
  const tableData = getValues(name);

  useEffect(() => {
    const eventName = "updateTable";
    const callback = ({ data, type } = {}) => {
      if (type === name) {
        const updatedValue = [
          ...data.map((row) => ({
            name: row.adunits,
            asset: { name: "" },
            venue: { name: "" },
          })),
          ...getValues(name),
        ];
        setValue(name, updatedValue);
        setIsTableUpdated(true);
      }
    };

    eventBus.on(eventName, callback);

    return () => {
      eventBus.remove(eventName, callback);
    };
  }, [name]);

  useEffect(() => {
    const updatedValue = getValues(name).map((adunit) => {
      const adunitObjectWithDetails = adunitObjectsMap[adunit.name];
      return adunitObjectWithDetails ? adunitObjectWithDetails : adunit;
    });

    setValue(name, updatedValue);
  }, [adunitObjectsMap, name]);

  const handleRemoveRows = () => {
    const removedAdunitNames = Object.keys(rowSelection);
    if (removedAdunitNames.length === 0) {
      return;
    }
    const updatedValue = getValues(name).filter(
      (row) => !removedAdunitNames.includes(row?.name)
    );
    setValue(name, updatedValue);
    setRowSelection({});
  };

  return (
    <>
      {isTableUpdated ? (
        <p className="adunit-table-help-text">
          Some of the columns will be empty for newly added Adunits. Data will
          be populated after the Placement is saved.
        </p>
      ) : null}
      <BasicTable
        errorNoResult={
          globalFilter
            ? null
            : {
                imgUrl: adunit_nodata,
                title: "Select Ad Units",
                description:
                  "To select ad units, keep ad units list handy and click on “Select Ad Unit” button above.",
              }
        }
        table_columns={[
          {
            accessorKey: "name",
            header: "Adunit Name",
          },
          {
            accessorKey: "asset.name",
            header: "Asset Name",
            enableSorting: isAdunitDataRetrieved,
          },
          {
            accessorKey: "venue.name",
            header: "Venue Name",
            enableSorting: isAdunitDataRetrieved,
          },
          {
            accessorKey: "id",
            header: "Adunit Id",
            accessorFn: (row) => (row?.id ? textTruncate(row?.id) : null),
            enableSorting: isAdunitDataRetrieved,
          },
        ]}
        renderHelpText={
          isEditMode && !isAdunitDataRetrieved ? (
            <>
              <Spinner color="primary" size={"sm"}>
                Loading...
              </Spinner>
              {"  "}
              Loading metadata...
              <ToolTips
                disabled={false}
                placement="right"
                text="We are retrieving the Asset Name, Venue Name and ID. Sorting will be fully functional once all data is loaded."
              >
                <Icon
                  data-testid="help-tooltip-icon"
                  icon={["fas", "info-circle"]}
                  id="loadingInfo"
                  size="1x"
                />
              </ToolTips>
            </>
          ) : null
        }
        getRowId={(row) => row?.name}
        table_data={tableData}
        enableRowSelection
        name="adunits"
        renderTopToolbarCustomActions={() => (
          <>
            <Button
              buttontext={`Remove (${
                Object.keys(rowSelection)?.length || 0
              }) Selected Rows`}
              icon={<Icon icon={["fas", "times"]} size="1x" />}
              onClick={handleRemoveRows}
              className="btn btn-secondary"
            />
            <FlatfileAdunits />
          </>
        )}
        state={{
          globalFilter,
          rowSelection,
        }}
        onGlobalFilterChange={setGlobalFilter}
        onRowSelectionChange={setRowSelection}
      />
    </>
  );
}
